<template>
  <div class="customer-autocomplete relative">
    <BaseAutoComplete
      v-model="model"
      :options="customers"
      v-bind="$attrs"
    >
      <template #first-option>
        <li 
          class="relative cursor-default select-none py-2 pl-8 pr-4 text-slate-900"
          @click="showCreateCustomerModal = true"
        >
          <span class="flex justify-between w-full">
            <span class="font-semibold">Create New</span>
            <span class="justify-self-end">
              <span class="h-5 w-5 i-heroicons-chevron-right" aria-hidden="true" />
            </span>
          </span>
        </li>
      </template>
    </BaseAutoComplete>

    <AppModal
      v-model="showCreateCustomerModal"
      title="Create New Customer"
    >
      <template #body>
        <CustomerForm :save-function="createCustomer" @cancel="showCreateCustomerModal = false" />
      </template>
    </AppModal>
  </div>
</template>

<script lang="ts" setup>
import { find } from 'underscore'
import { ref, computed, watch } from 'vue'
import { Customer } from '@/models/customer'
import CustomerService from '@/services/CustomerService'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import BaseAutoComplete from './BaseAutoComplete.vue'
import AppModal from '../app/AppModal.vue'
import CustomerForm from '@/modules/Customer/components/CustomerForm.vue'
import { useNotification } from '@kyvg/vue3-notification'

const selectedCustomer = defineModel({
  type: String,
  default: ''
})

const emit = defineEmits(['change'])
const showCreateCustomerModal = ref(false)
const { notify } = useNotification()
const queryClient = useQueryClient()
const customerService = new CustomerService()

const { data: customers } = useQuery({
  queryKey: ['customers'],
  queryFn: () => customerService.all().then((customers) => {
    return  customers.filter((customer) => customer.name).map((customer) => ({ label: customer.name, value: customer.id, ...customer }))
  })
})

const { mutate: createCustomer } = useMutation({
  mutationFn: (newCustomer: Customer) => customerService.create(newCustomer),
  onSettled(data) {
    if (data) {
      model.value = { label: data.name, value: data.id, ...data }
    }
    showCreateCustomerModal.value = false
  },
  onSuccess() { 
    queryClient.invalidateQueries({ queryKey: ['customers']})
  },
  onError() {
    notify({ type: 'error', text: 'Error creating customer'})
  }  
})

const model = computed({
  get: () => {
    if (customers.value) {
      return find(customers.value, (customer) => customer.value === selectedCustomer.value)
    }
    return null
  },
  set: (value) => {
    selectedCustomer.value = value.value
  }
})

watch(model, () => {
  if (model.value) {
    console.log(model.value)
    selectedCustomer.value = model.value.value
  }
  emit('change', model.value)
})

</script>
